import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { LogIn, ShieldCheck, Lock, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

// API adresini garantiye alıyoruz
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://mertt.tr";
const API = `${BACKEND_URL}/api`;

function AdminLogin() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // --- STATE'LER ---
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    two_fa_code: '' 
  });
  const [loading, setLoading] = useState(false);
  
  // Şifre Sıfırlama İçin Eklenen State'ler
  const [isResetting, setIsResetting] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState(''); 
  const [newPassword, setNewPassword] = useState('');

  // --- GİRİŞ İŞLEMİ ---
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, credentials);
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('Giriş başarılı! Komuta merkezine yönlendiriliyorsunuz...');
      
      navigate('/mertt-komuta-merkezi-99/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş reddedildi! Şifre veya 2FA kodu hatalı.');
    } finally {
      setLoading(false);
    }
  };

  // --- ŞİFRE SIFIRLAMA İŞLEMİ (2FA İLE) ---
  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    // KONTROL: Şifre sıfırlama öncesi anlık doğrulama
    if (twoFaCode.length !== 6) {
      toast.error("2FA Kodu tam olarak 6 haneli bir rakam olmalıdır!");
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/sifirla`, {
        email: credentials.email,
        secret_key: twoFaCode, 
        new_password: newPassword
      });
      
      toast.success('Harika! Şifreniz 2FA onayıyla başarıyla güncellendi.');
      setIsResetting(false); 
      setCredentials({ ...credentials, password: '', two_fa_code: '' }); 
      setTwoFaCode('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google Authenticator kodu yanlış veya mail bulunamadı!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen flex items-center justify-center relative overflow-hidden"
      style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1485740112426-0c2549fa8c86)',
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-slate-900/90"></div>

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="text-center mb-8">
          <h1 className="font-emblema text-5xl text-gradient mb-2">MERTT</h1>
          <p className="text-gray-400">
            {isResetting ? "GÜVENLİK PROTOKOLÜ: SIFIRLAMA" : t('admin.dashboard')}
          </p>
        </div>

        <div className="glass p-8 border border-slate-700/50 shadow-2xl rounded-2xl relative overflow-hidden">
          {/* Süsleme */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-cyan-500 to-blue-500"></div>

          {/* ================= GİRİŞ EKRANI ================= */}
          {!isResetting ? (
            <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wider">
                  Sistem Email
                </label>
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                  placeholder="admin@mertt.com"
                  className="w-full bg-slate-800/50 border border-slate-700 focus:border-cyan-500 text-white px-4 py-3 rounded-lg outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wider">
                  Parola
                </label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  placeholder="••••••••"
                  className="w-full bg-slate-800/50 border border-slate-700 focus:border-cyan-500 text-white px-4 py-3 rounded-lg outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-cyan-400 mb-1 uppercase tracking-wider flex items-center gap-2">
                  <ShieldCheck size={14} /> 2FA Doğrulama Kodu
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength="6"
                  value={credentials.two_fa_code}
                  onChange={(e) => {
                    // SADECE RAKAMLARI KABUL ET
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    setCredentials({ ...credentials, two_fa_code: numericValue });
                  }}
                  placeholder="Google Authenticator (6 Hane)"
                  className="w-full bg-slate-900/80 border border-cyan-900/50 focus:border-cyan-400 text-cyan-400 text-center tracking-[0.5em] font-mono px-4 py-3 rounded-lg outline-none transition"
                />
              </div>

              <div className="flex justify-end">
                <button 
                  type="button" 
                  onClick={() => setIsResetting(true)} 
                  className="text-xs text-gray-500 hover:text-cyan-400 transition"
                >
                  Şifremi Unuttum / Sıfırla
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="bg-cyan-600 hover:bg-cyan-500 w-full flex items-center justify-center space-x-2 py-3 rounded-lg font-bold transition text-white shadow-lg shadow-cyan-600/20"
              >
                {loading ? (
                  <div className="spinner"></div>
                ) : (
                  <>
                    <LogIn size={18} />
                    <span>GÜVENLİ GİRİŞ</span>
                  </>
                )}
              </button>
            </form>
          ) : (
            
            /* ================= ŞİFRE SIFIRLAMA EKRANI ================= */
            <form onSubmit={handleReset} className="space-y-5">
              <div>
                <label className="block text-xs font-semibold text-gray-400 mb-1 uppercase tracking-wider">Admin Email</label>
                <input
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                  placeholder="Sistemdeki mailiniz"
                  className="w-full bg-slate-800/50 border border-slate-700 focus:border-purple-500 text-white px-4 py-3 rounded-lg outline-none transition"
                />
              </div>

              <div className="bg-purple-900/20 p-4 rounded-lg border border-purple-500/30">
                <label className="block text-xs font-semibold text-purple-400 mb-2 uppercase tracking-wider flex items-center gap-2">
                  <ShieldCheck size={14} /> Google Authenticator Kodu
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength="6"
                  value={twoFaCode}
                  onChange={(e) => {
                     // SADECE RAKAMLARI KABUL ET
                     const numericValue = e.target.value.replace(/[^0-9]/g, '');
                     setTwoFaCode(numericValue);
                  }}
                  required
                  placeholder="6 Haneli 2FA Kodu"
                  className="w-full bg-slate-900 border border-purple-700/50 focus:border-purple-400 text-purple-300 text-center tracking-[0.5em] font-mono px-4 py-3 rounded-lg outline-none transition"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-green-400 mb-1 uppercase tracking-wider flex items-center gap-2">
                  <Lock size={14} /> Yeni Şifreniz
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  placeholder="Yeni belirlediğiniz şifre"
                  className="w-full bg-slate-800/50 border border-green-900/50 focus:border-green-500 text-white px-4 py-3 rounded-lg outline-none transition"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsResetting(false)}
                  className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-lg transition flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={18} /> Geri
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-[2] bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-bold py-3 rounded-lg transition"
                >
                  {loading ? <div className="spinner mx-auto"></div> : 'Şifreyi Güncelle'}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}

export default AdminLogin;